import React, { ReactElement, useEffect } from "react";
import { useSnackbar } from "notistack";
import { Controller } from "react-hook-form";

import {
  Grid,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  Tooltip,
  Autocomplete as MuiAutocomplete,
  AutocompleteRenderInputParams,
  Typography,
} from "@mui/material";
import { History as HistoryIcon } from "@mui/icons-material";
import { CheckField, NumericField, Autocomplete, InfoPopper } from "core";

import { useStateMachine } from "little-state-machine";

import {
  EMPTY_STRING,
  MANDATORY_LABEL,
  EMAIL_EXISTS_VALIDATION_MESSAGE,
  EMAIL_FORMAT_MISSING_VALIDATION_MESSAGE,
} from "shared/constants";
import { getFieldErrorMessage, getFieldHasError } from "shared/utils";
import { FillFormOptionType } from "master-data/enums";
import { FormStandardFieldProps } from "../props";

export interface FormFinancialEpdsProps extends FormStandardFieldProps {
  /**
   * watch
   */
  watch: any;
  /**
   * setValue
   */
  setValue: any;
  /**
   * getValues
   */
  getValues: any;
  /**
  /**
   * Fill form option type
   */
  fillOption: FillFormOptionType;
}

export const FormFinancialEpds = ({
  isEdit,
  control,
  errors,
  revisionValues,
  watch,
  setValue,
  getValues,
  fillOption,
}: FormFinancialEpdsProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const anchorRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const {
    state: { clientDocumentData, cacheData },
  } = useStateMachine();

  const { isContract } = clientDocumentData;
  const { enumEarlyPaymentDiscount, enumEpdReceived } = cacheData;

  const epdWatch = watch("earlyPaymentDiscountId");
  const isEpdDisabled = typeof epdWatch === "number" && epdWatch === 1;

  // #region Effects
  useEffect(() => {
    if (isEpdDisabled) {
      setValue("epdReceivedId", null, { shouldValidate: true });
      setValue("percentagePassedOn", null, { shouldValidate: true });
      setValue("epdReceivedPaidWithinNumberOfDays", null, {
        shouldValidate: true,
      });
      setValue("percentagePaidWithinNumberOfDays", null, {
        shouldValidate: true,
      });
      setValue("additionalConditionsInContract", false, {
        shouldValidate: true,
      });
    }
  }, [setValue, epdWatch, isEpdDisabled]);

  useEffect(() => {
    if (fillOption) {
      if (
        fillOption === FillFormOptionType.fillMandatory ||
        fillOption === FillFormOptionType.fillMandatoryAndSave
      ) {
        if (enumEarlyPaymentDiscount[0]) {
          setValue("earlyPaymentDiscountId", enumEarlyPaymentDiscount[0].key);
        }
      } else if (enumEarlyPaymentDiscount[1]) {
        setValue("earlyPaymentDiscountId", enumEarlyPaymentDiscount[1].key);
      }

      if (
        fillOption === FillFormOptionType.fillAll ||
        fillOption === FillFormOptionType.fillAllAndSave
      ) {
        setValue("percentagePassedOn", 10);
        setValue("epdReceivedPaidWithinNumberOfDays", 15);
        setValue("percentagePaidWithinNumberOfDays", 3);
        setValue("additionalConditionsInContract", true);
        if (enumEpdReceived[0]) {
          setValue("epdReceivedId", enumEpdReceived[0].key);
        }
      }
    }
  }, [
    fillOption,
    setValue,
    isContract,
    enumEarlyPaymentDiscount,
    enumEpdReceived,
  ]);
  // #endregion Effects

  // #region Methods
  const handleClick = () => {
    setOpen(true);
  };

  const handleClickAway = () => {
    setOpen(false);
  };
  // #endregion Methods

  return (
    <Grid container spacing={1}>
      <Grid item xs={6}>
        <Autocomplete
          autoHighlight
          id="earlyPaymentDiscountId"
          name="earlyPaymentDiscountId"
          label={`
            Early Payment Discount 
              ${isContract ? MANDATORY_LABEL : EMPTY_STRING}`}
          options={enumEarlyPaymentDiscount}
          error={!!errors.earlyPaymentDiscountId?.message}
          helperText={errors.earlyPaymentDiscountId?.message}
          revisionValue={revisionValues?.EarlyPaymentDiscountId}
          disabled={!isEdit}
          control={control}
        />
      </Grid>
      <Grid item xs={6}>
        <Autocomplete
          autoHighlight
          id="epdReceivedId"
          name="epdReceivedId"
          label="EPD Received to be Passed on"
          options={enumEpdReceived}
          error={!!errors.epdReceivedId?.message}
          helperText={errors.epdReceivedId?.message}
          revisionValue={revisionValues?.EpdReceivedId}
          disabled={!isEdit || isEpdDisabled}
          control={control}
        />
      </Grid>
      <Grid item xs={6}>
        <NumericField
          id="percentagePassedOn"
          name="percentagePassedOn"
          label="Percentage Passed on"
          fullWidth
          disabled={!isEdit || isEpdDisabled}
          error={!!errors.percentagePassedOn?.message}
          helperText={errors.percentagePassedOn?.message}
          revisionValue={revisionValues?.PercentagePassedOn}
          control={control}
          percentage
        />
      </Grid>
      <Grid item xs={6}>
        <NumericField
          id="epdReceivedPaidWithinNumberOfDays"
          name="epdReceivedPaidWithinNumberOfDays"
          label="Paid Within Number of Days"
          fullWidth
          disabled={!isEdit || isEpdDisabled}
          error={!!errors.epdReceivedPaidWithinNumberOfDays?.message}
          helperText={errors.epdReceivedPaidWithinNumberOfDays?.message}
          revisionValue={revisionValues?.EpdReceivedPaidWithinNumberOfDays}
          control={control}
          integer
        />
      </Grid>
      <Grid item xs={6}>
        <NumericField
          id="percentagePaidWithinNumberOfDays"
          name="percentagePaidWithinNumberOfDays"
          label="Paid Within Number of Days"
          fullWidth
          disabled={!isEdit || isEpdDisabled}
          error={!!errors.percentagePaidWithinNumberOfDays?.message}
          helperText={errors.percentagePaidWithinNumberOfDays?.message}
          revisionValue={revisionValues?.PercentagePaidWithinNumberOfDays}
          control={control}
          integer
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name="epdContactEmail"
          control={control}
          render={({ field }) => (
            <Tooltip
              title={
                !isEdit && getValues("epdContactEmail")
                  ? getValues("epdContactEmail").join(", ")
                  : EMPTY_STRING
              }
              placement="bottom"
            >
              <MuiAutocomplete
                {...field}
                autoHighlight
                freeSolo
                multiple
                id="epdContactEmail"
                filterSelectedOptions
                options={[]}
                ChipProps={{ color: "primary" }}
                clearOnBlur
                renderInput={(params: AutocompleteRenderInputParams) => {
                  const revisionValue =
                    revisionValues?.EpdContactEmail?.replace(
                      /(^.*\[|\].*$)/g,
                      EMPTY_STRING
                    ).replace(/['"]+/g, EMPTY_STRING);
                  return (
                    <>
                      <MuiTextField
                        {...params}
                        name="epdContactEmail"
                        label="EPD Contact Email Address (Max.2) * (Type the email address and press enter...)"
                        variant="outlined"
                        error={getFieldHasError(errors.epdContactEmail)}
                        helperText={getFieldErrorMessage(
                          errors.epdContactEmail
                        )}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: revisionValue ? (
                            <>
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle change value visibility"
                                  onClick={handleClick}
                                  ref={anchorRef}
                                >
                                  <HistoryIcon color="primary" />
                                </IconButton>
                              </InputAdornment>
                              {params.InputProps.endAdornment}
                            </>
                          ) : (
                            params.InputProps.endAdornment
                          ),
                        }}
                      />
                      {revisionValue && (
                        <InfoPopper
                          text={revisionValue}
                          onClickAway={handleClickAway}
                          open={open}
                          anchorEl={anchorRef.current}
                        />
                      )}
                    </>
                  );
                }}
                onChange={(_, data, reason, detail) => {
                  if (
                    reason === "createOption" &&
                    field.value.includes(detail?.option.trim())
                  ) {
                    enqueueSnackbar(EMAIL_EXISTS_VALIDATION_MESSAGE, {
                      variant: "warning",
                    });
                  } else {
                    const newData = data.map((x) => x.trim());
                    field.onChange(newData);
                  }
                }}
                onClose={(event) => {
                  if (!event.defaultPrevented) {
                    enqueueSnackbar(EMAIL_FORMAT_MISSING_VALIDATION_MESSAGE, {
                      variant: "warning",
                    });
                  }
                }}
                disabled={!isEdit}
              />
            </Tooltip>
          )}
        />
        <Typography variant="subtitle2" color="red">
          EPD Notification letter Contact Email Address (Client Contact) is
          mandatory for all non-intercompany media clients!
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <CheckField
          control={control}
          disabled={!isEdit || isEpdDisabled}
          name="additionalConditionsInContract"
          label="Additional Conditions In Contract"
          revisionValue={revisionValues?.AdditionalConditionsInContract}
        />
      </Grid>
    </Grid>
  );
};

export default FormFinancialEpds;
